import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Divider, Link, Typography } from "@mui/material";
import {
  ArrowForward,
  PsychologyAlt,
  StorageOutlined as StorageIcon,
  HubOutlined as IntegrationsTileIcon,
  AutoStoriesOutlined as PartnerDirectoryIcon,
  DriveFolderUploadOutlined as SecureUploadTileIcon,
} from "@mui/icons-material";
import Layout from "components/Layout";
import PartnerSpotlightCarousel from "components/landing/PartnerSpotlightCarousel";
import ShellMainContentPanel from "components/shell/ShellMainContentPanel";
import { useGetUserQuery } from "features/auth/auth";
import { useGetPartnersQuery } from "features/partners/partnersApi";
import { useAppSelector } from "features/store";
import { useViewAs } from "hooks/useViewAs";
import { usePermissions } from "hooks/usePermissions";
import { ACTIONS } from "utils/access";
import { integrationsHomePath } from "utils/roles";
import { useTheme } from "@mui/material/styles";
import {
  CALIBRATE_SURFACE_HAIRLINE,
  CALIBRATE_FONT_FAMILY_ACCENT,
} from "components/hoc/Theme";
import { VIEWPORT_2K_MIN_MEDIA, VIEWPORT_4K_MIN_MEDIA } from "config";

const LEGACY_STORAGE_PARAMS = [
  "prefix",
  "page",
  "search",
  "folder_type",
  "sort_field",
  "sort_order",
  "time_display",
] as const;

const HERO_HEADING = "Welcome to your Calibrate HCM Client Portal" as const;
const HERO_SUBHEADER = "Your tools and resources, in one place." as const;

const PARTNER_SECTION_TITLE = "Partner Ecosystem" as const;
const PARTNER_SECTION_SUBHEADER =
  "We collaborate with respected organizations across multiple industries to build value for you." as const;

const COPY = {
  admin: {
    integrations:
      "Monitor live integrations, their history, and their schedules.",
    dataStorage:
      "Access files from data migrations stored securely on your account.",
    ai: "Explore your workforce data across every system.",
    subtitle: HERO_SUBHEADER,
  },
  client: {
    integrations:
      "Monitor live integrations, their history, and their schedules.",
    dataStorage:
      "Access files from data migrations stored securely on your account.",
    ai: "Explore your workforce data across every system.",
    subtitle: HERO_SUBHEADER,
  },
} as const;

type TileIconKind =
  | "integrations"
  | "storage"
  | "chat"
  | "partners"
  | "secureUpload";

type LandingTile = {
  key: string;
  title: string;
  description: string;
  cta: string;
  to: string;
  iconKind: TileIconKind;
};

function TileIcon({ kind }: { kind: TileIconKind }) {
  const sx = {
    fontSize: 26,
    color: "currentColor",
    [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 34 },
    [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 36 },
  };
  switch (kind) {
    case "integrations":
      return <IntegrationsTileIcon sx={sx} />;
    case "storage":
      return <StorageIcon sx={sx} />;
    case "chat":
      return <PsychologyAlt sx={sx} />;
    case "partners":
      return <PartnerDirectoryIcon sx={sx} />;
    case "secureUpload":
      return <SecureUploadTileIcon sx={sx} />;
    default:
      return null;
  }
}

/** Default tile (~1080p / FHD): compact for taskbar + partner visibility. */
const TILE_W = 216;
const TILE_H = 216;
/** ~2200px+ ("large desktop" / 2K-class): ~280px tiles; 4K tier keeps historical 304:272 step-up. */
const TILE_W_2K = 280;
const TILE_H_2K = TILE_W_2K;
const GRID_COL_GAP = 28;
const GRID_ROW_GAP = 14;
const GRID_COL_GAP_2K = 38;
const GRID_ROW_GAP_2K = 19;
const TILE_W_4K = Math.round((TILE_W_2K * 304) / 272);
const TILE_H_4K = TILE_W_4K;
const GRID_COL_GAP_4K = 40;
const GRID_ROW_GAP_4K = 20;

export default function PlatformHomePage() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isClientView, profile, href } = useViewAs();
  const { sees, seesSurface } = usePermissions();

  const webAccessDenied = useAppSelector((s) => s.auth.webAccessDenied);
  const { isLoading } = useGetUserQuery(undefined, {
    skip: !!webAccessDenied,
  });

  // The partner logos come from the Partner Directory, which not every group opens.
  const { data: partnersData } = useGetPartnersQuery(
    { page_size: 1000 },
    { skip: !seesSurface("partner_directory") },
  );
  const allPartners = partnersData?.results || [];

  const partnersWithLogos = useMemo(() => {
    return allPartners.filter(
      (partner) => partner.logo_url && partner.logo_url.trim() !== "",
    );
  }, [allPartners]);

  const queryString = searchParams.toString();

  useEffect(() => {
    const st = location.state as { initialPrefix?: string } | undefined;
    const suffix = queryString ? `?${queryString}` : "";
    if (st?.initialPrefix) {
      navigate(href(`/data-storage${suffix}`), { replace: true, state: st });
      return;
    }

    const hasLegacy = LEGACY_STORAGE_PARAMS.some((k) => searchParams.has(k));
    if (!hasLegacy) return;
    navigate(href(`/data-storage${suffix}`), { replace: true });
  }, [location.state, queryString, searchParams, href, navigate]);

  const useClientFacingCopy = profile?.user_type !== "internal";
  const text = useClientFacingCopy ? COPY.client : COPY.admin;

  const productTiles = useMemo(() => {
    const products: LandingTile[] = [];

    const integrations = seesSurface("integrations");
    const dataStorage = seesSurface("data_storage");
    const ai = sees(ACTIONS.AI_AGENT_OPEN);
    const partners = seesSurface("partner_directory");
    const secureUpload = seesSurface("secure_exchange");

    if (dataStorage) {
      products.push({
        key: "storage",
        title: "Data Storage",
        description: text.dataStorage,
        cta: "Open Storage",
        to: href("/data-storage"),
        iconKind: "storage",
      });
    }
    if (integrations) {
      products.push({
        key: "integrations",
        title: "Integrations",
        description: text.integrations,
        cta: "View Integrations",
        to: href(integrationsHomePath(profile)),
        iconKind: "integrations",
      });
    }
    if (ai) {
      products.push({
        key: "chat",
        title: "AI Reporting",
        description: text.ai,
        cta: "Launch AI Tool",
        to: href("/chat"),
        iconKind: "chat",
      });
    }

    if (partners) {
      products.push({
        key: "partners",
        title: "Partner Directory",
        description:
          "Browse Calibrate HCM's different service and integration partners.",
        cta: "Open Directory",
        to: href("/partners"),
        iconKind: "partners",
      });
    }

    if (secureUpload) {
      products.push({
        key: "secureUpload",
        title: "Secure Exchange",
        description: "Share files securely with your CalibrateHCM team.",
        cta: "Open Exchange",
        to: href("/secure-exchange"),
        iconKind: "secureUpload",
      });
    }

    return products;
  }, [sees, seesSurface, href, text]);

  const accountSettingsPath = href("/account-settings");

  if (!isClientView && isLoading) {
    return (
      <Layout title="Home" content="Home">
        <ShellMainContentPanel>
          <Box sx={{ py: 8, textAlign: "center", width: "100%" }}>
            <Typography color="text.secondary">Loading…</Typography>
          </Box>
        </ShellMainContentPanel>
      </Layout>
    );
  }

  const noProducts = productTiles.length === 0;

  const cardBorder = `1px solid ${CALIBRATE_SURFACE_HAIRLINE}`;

  const tileCellSx = { width: "100%", minWidth: 0 };

  const renderTile = (t: LandingTile) => {
    const inner = (
      <Box
        onClick={() => navigate(t.to)}
        sx={{
          width: "100%",
          height: TILE_H,
          boxSizing: "border-box",
          p: 1.75,
          bgcolor: "background.paper",
          borderRadius: 1,
          border: cardBorder,
          boxShadow: theme.shadows[2],
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          cursor: "pointer",
          transition: "box-shadow 0.2s ease",
          [VIEWPORT_2K_MIN_MEDIA]: {
            height: TILE_H_2K,
            p: 2.5,
          },
          [VIEWPORT_4K_MIN_MEDIA]: {
            height: TILE_H_4K,
            p: 2.5,
          },
          "&:hover": {
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: "calibrate.iconContainer",
              color: "calibrate.iconOnTeal",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              [VIEWPORT_2K_MIN_MEDIA]: {
                width: 62,
                height: 62,
              },
              [VIEWPORT_4K_MIN_MEDIA]: {
                width: 64,
                height: 64,
              },
            }}
          >
            <TileIcon kind={t.iconKind} />
          </Box>
          <Typography
            sx={{
              ...theme.typography.calibrateDisplay,
              fontSize: 17,
              fontWeight: 700,
              color: "text.primary",
              lineHeight: 1.2,
              [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 22 },
              [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 24 },
            }}
          >
            {t.title}
          </Typography>
          <Typography
            sx={{
              ...theme.typography.calibrateAccent,
              fontSize: 11,
              fontWeight: 400,
              letterSpacing: 0,
              color: "text.secondary",
              lineHeight: 1.5,
              flexShrink: 0,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 14 },
              [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 15 },
            }}
          >
            {t.description}
          </Typography>
        </Box>
        <Box sx={{ flex: "1 1 0", minHeight: 0 }} />
        <Link
          component="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(t.to);
          }}
          sx={{
            mt: 0,
            flexShrink: 0,
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            ...theme.typography.calibrateDisplay,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.2,
            color: "calibrate.iconContainer",
            textDecoration: "none",
            cursor: "inherit",
            border: "none",
            background: "none",
            padding: 0,
            [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 18 },
            [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 19 },
            "&:hover": { color: "secondary.dark" },
          }}
        >
          <Box component="span">{t.cta}</Box>
          <ArrowForward
            aria-hidden
            sx={{
              flexShrink: 0,
              color: "inherit",
              fontSize: "1.12em",
              width: "1.12em",
              height: "1.12em",
              transform: "translateY(-0.06em)",
              display: "block",
            }}
          />
        </Link>
      </Box>
    );

    return (
      <Box key={t.key} sx={tileCellSx}>
        {inner}
      </Box>
    );
  };

  return (
    <Layout title="Home" content="Home">
      <ShellMainContentPanel>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            px: { xs: 2, sm: 3, md: 5 },
            pt: { xs: 3, md: 5 },
            pb: { xs: 2, md: 3 },
            [VIEWPORT_2K_MIN_MEDIA]: {
              px: { md: 6 },
              pt: { md: 5.5 },
              pb: { md: 3.5 },
            },
            [VIEWPORT_4K_MIN_MEDIA]: {
              px: { md: 7 },
              pt: { md: 6 },
              pb: { md: 4 },
            },
          }}
        >
          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                mb: { xs: 4, md: 1.5 },
                width: "100%",
                boxSizing: "border-box",
                textAlign: "left",
                [VIEWPORT_2K_MIN_MEDIA]: { mb: 4 },
              }}
            >
              <Typography
                component="h1"
                sx={{
                  ...theme.typography.calibrateDisplay,
                  fontSize: { xs: "1.75rem", md: "36px" },
                  fontWeight: 700,
                  color: "calibrate.heroTitle",
                  lineHeight: 1.2,
                  textAlign: "left",
                  [VIEWPORT_2K_MIN_MEDIA]: { fontSize: "44px" },
                  [VIEWPORT_4K_MIN_MEDIA]: { fontSize: "48px" },
                }}
              >
                {HERO_HEADING}
              </Typography>
              <Typography
                sx={{
                  mt: { xs: 1, md: 0.75 },
                  ...theme.typography.calibrateDisplay,
                  fontSize: { xs: "1.125rem", md: "24px" },
                  fontWeight: 400,
                  color: "calibrate.heroSubtitle",
                  lineHeight: 1.35,
                  textAlign: "left",
                  [VIEWPORT_2K_MIN_MEDIA]: { fontSize: "30px" },
                  [VIEWPORT_4K_MIN_MEDIA]: { fontSize: "32px" },
                }}
              >
                {text.subtitle}
              </Typography>
            </Box>

            {noProducts ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 2,
                  maxWidth: 560,
                  mx: "auto",
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>
                  You do not have access to any Calibrate products. Please
                  contact a Calibrate team member to help.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(accountSettingsPath)}
                >
                  Account settings
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: `repeat(2, ${TILE_W}px)`,
                    lg: `repeat(4, ${TILE_W}px)`,
                  },
                  columnGap: `${GRID_COL_GAP}px`,
                  rowGap: `${GRID_ROW_GAP}px`,
                  justifyContent: "start",
                  width: "100%",
                  pt: { xs: 1, md: 0 },
                  [VIEWPORT_2K_MIN_MEDIA]: {
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      sm: `repeat(2, ${TILE_W_2K}px)`,
                      lg: `repeat(4, ${TILE_W_2K}px)`,
                    },
                    columnGap: `${GRID_COL_GAP_2K}px`,
                    rowGap: `${GRID_ROW_GAP_2K}px`,
                    pt: 1,
                  },
                  [VIEWPORT_4K_MIN_MEDIA]: {
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      sm: `repeat(2, ${TILE_W_4K}px)`,
                      lg: `repeat(4, ${TILE_W_4K}px)`,
                    },
                    columnGap: `${GRID_COL_GAP_4K}px`,
                    rowGap: `${GRID_ROW_GAP_4K}px`,
                  },
                }}
              >
                {productTiles.map((t) => renderTile(t))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              width: "100%",
              mt: "auto",
              pt: { xs: 2, md: 2.75 },
            }}
          >
            <Divider
              sx={{
                borderColor: CALIBRATE_SURFACE_HAIRLINE,
                mb: { xs: 1.25, md: 2 },
              }}
            />
            <Box
              sx={{
                pb: { xs: 0.5, md: 0.75 },
                width: "100%",
                maxWidth: "100%",
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              <Typography
                sx={{
                  ...theme.typography.calibrateDisplay,
                  fontSize: { xs: 20, sm: 22 },
                  fontWeight: 400,
                  color: "text.secondary",
                  [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 30 },
                  [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 32 },
                }}
              >
                {PARTNER_SECTION_TITLE}
              </Typography>
              <Typography
                sx={{
                  mt: { xs: 0.5, md: 0.75 },
                  ...theme.typography.calibrateDisplay,
                  fontSize: { xs: 12.5, sm: 13 },
                  fontWeight: 400,
                  color: "text.secondary",
                  lineHeight: 1.5,
                  [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 17 },
                  [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 18 },
                }}
              >
                {PARTNER_SECTION_SUBHEADER}
              </Typography>
              <PartnerSpotlightCarousel partners={partnersWithLogos} />
              <Link
                href={href("/partners")}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 2,
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "calibrate.helperAccent",
                  textDecoration: "none",
                  fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                  "&:hover": {
                    textDecoration: "underline",
                    fontSize: "0.9375rem",
                  },
                  transition: "font-size 0.2s ease",
                }}
              >
                Explore Partnerships
                <ArrowForward sx={{ fontSize: 16 }} />
              </Link>
            </Box>
          </Box>
        </Box>
      </ShellMainContentPanel>
    </Layout>
  );
}
